// Email Contact Types
export interface EmailContact {
  id: number;
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  email: string;
  isGlobal: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateContactData {
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  email: string;
}

// Email Template Types
export interface EmailTemplate {
  id: number;
  name: string;
  greetingText?: string;
  closingText?: string;
  isDefault?: boolean;
  isGlobal: boolean;
  isSystemTemplate?: boolean; // deprecated, use isGlobal
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateTemplateData {
  name: string;
  greetingText: string;
  closingText: string;
  isDefault: boolean;
}

// Merge Fields
export type MergeField = {
  key: string;
  label: string;
  example: string;
};

export const MERGE_FIELDS: MergeField[] = [
  { key: "{{firstName}}", label: "Contact's First Name", example: "John" },
  { key: "{{artistName}}", label: "Artist Name", example: "The Beatles" },
  { key: "{{configuration}}", label: "Configuration", example: "Single" },
  { key: "{{releaseTitle}}", label: "Release Title", example: "Hey Jude" },
  { key: "{{releaseDate}}", label: "Release Date", example: "2024-03-15" },
  { key: "{{genre}}", label: "Genre", example: "Rock" },
  { key: "{{company}}", label: "Contact's Company", example: "Spotify" },
  { key: "{{personalizedMessage}}", label: "Personalized Message", example: "Your custom message for this contact" },
];

// Personalized Message for a contact
export interface PersonalizedMessage {
  contactId: number;
  message: string;
}

// Email data with personalized messages support
export interface EmailModalData {
  contactIds: number[];
  templateId: string;
  subject: string;
  templateContent?: string;
  selectedContacts?: EmailContact[];
  personalizedMessages?: PersonalizedMessage[];
}

// CSV Upload Types
export interface CSVUploadError {
  row: number;
  email: string;
  reason: string;
}

export interface CSVUploadResult {
  addedCount: number;
  failedCount: number;
  errors: CSVUploadError[];
}

// OAuth Status
export interface ProviderConnection {
  connected: boolean;
  email: string | null;
  connectedAt: string | null;
  lastRefreshedAt: string | null;
}

export interface OAuthStatus {
  gmail: ProviderConnection;
  outlook: ProviderConnection;
}

// Contact Table Props
export interface ContactTableProps {
  contacts: EmailContact[];
  onEdit: (contact: EmailContact) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
}

// Template Card Props
export interface TemplateCardProps {
  template: EmailTemplate;
  onEdit: (template: EmailTemplate) => void;
  onDelete: (id: number) => void;
  onSetDefault: (id: number) => void;
}

// Pitch Body Template Types
export interface PitchBodyField {
  key: string;
  label: string;
  order: number;
  enabled: boolean;
}

export interface PitchBodyTemplate {
  id: string;
  name: string;
  fields: PitchBodyField[];
  isDefault?: boolean;
  isGlobal?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Fields that always appear in the header line above the pitch body (Artist Name - Release Title - Release Date)
// and cannot be disabled or reordered in the Pitch Body customizer.
export const REQUIRED_PITCH_BODY_FIELD_KEYS = ['artistName', 'releaseTitle', 'releaseDate'] as const;

// What the fixed default block (getPitchDataHtml) contains when no pitch body is selected.
export const DEFAULT_PITCH_BLOCK_FIELDS: Omit<PitchBodyField, 'order' | 'enabled'>[] = [
  { key: 'artistName', label: 'Artist Name' },
  { key: 'releaseTitle', label: 'Release Title' },
  { key: 'releaseDate', label: 'Release Date' },
  { key: 'promoPhotos', label: 'Promo Photos/Images' },
  { key: 'linkToListen', label: 'Link to Listen' },
  { key: 'releaseUpc', label: 'Release UPC' },
  { key: 'trackIsrc', label: 'ISRC' },
  { key: 'mainGenre', label: 'Genre' },
  { key: 'songMood', label: 'Song Mood' },
  { key: 'shortDescription', label: 'Description' },
  { key: 'longDescription', label: 'Marketing Drivers' },
];

// Every field a pitch body can include: the pitch form fields, then the roster fields (AD-240).
export const ALL_PITCH_BODY_FIELDS: Omit<PitchBodyField, 'order' | 'enabled'>[] = [
  { key: 'artistName', label: 'Artist Name' },
  { key: 'releaseTitle', label: 'Release Title' },
  { key: 'releaseDate', label: 'Release Date' },
  { key: 'configuration', label: 'Configuration' },
  { key: 'releaseUpc', label: 'Release UPC' },
  { key: 'focusTrackName', label: 'Focus Track' },
  { key: 'trackIsrc', label: 'ISRC' },
  { key: 'featuredArtists', label: 'Featured Artists' },
  { key: 'shortDescription', label: 'Description' },
  { key: 'longDescription', label: 'Marketing Drivers' },
  { key: 'songMood', label: 'Song Mood' },
  { key: 'linkToListen', label: 'Link to Listen' },
  { key: 'promoPhotos', label: 'Promo Photos/Images' },
  { key: 'language', label: 'Language' },
  { key: 'spotifySubgenre', label: 'Spotify Subgenre' },
  { key: 'instruments', label: 'Instruments' },
  { key: 'similarArtists', label: 'Similar Artists' },
  { key: 'spotifyPlaylist', label: 'Spotify Playlist' },
  { key: 'deezerPlaylist', label: 'Deezer Playlist' },
  { key: 'TIDALpage', label: 'Link to TIDAL Artist Page' },
  { key: 'distributor', label: 'Distributor' },
  { key: 'tidalDolbyAtmos', label: 'Delivered in Dolby Atmos' },
  { key: 'appleReleaseStage', label: 'Release Stage' },
  { key: 'playlistConsideration', label: 'SoundCloud Playlist Consideration' },
  { key: 'appleId', label: 'Apple Artist ID' },
  { key: 'appleTrackId', label: 'Apple Track ID' },
  { key: 'appleAlbumId', label: 'Apple Album ID' },
  { key: 'asin', label: 'ASIN' },
  { key: 'songWriters', label: 'Songwriters' },
  { key: 'contributors', label: 'Contributors' },
  { key: 'spotifyURI', label: 'Spotify URI' },
  { key: 'appleID', label: 'Apple ID' },
  { key: 'artistAsin', label: 'Artist ASIN' },
  { key: 'soundcloudURL', label: 'SoundCloud URL' },
  { key: 'tidalURL', label: 'TIDAL URL' },
  { key: 'bio', label: 'Bio' },
  { key: 'instagram', label: 'Instagram URL' },
  { key: 'tiktok', label: 'TikTok URL' },
  { key: 'twitter', label: 'X URL' },
  { key: 'facebook', label: 'Facebook URL' },
  { key: 'snapchat', label: 'Snapchat URL' },
  { key: 'spotifyURL', label: 'Spotify URL' },
  { key: 'appleURL', label: 'Apple Music URL' },
  { key: 'amazonURL', label: 'Amazon URL' },
  { key: 'deezerURL', label: 'Deezer URL' },
];
